import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import Badge from "react-bootstrap/Badge";
import Form from "react-bootstrap/Form";
import Spinner from "components/Spinner";
import ConfirmationModal from "components/ConfirmationModal";
import { DraggableModalDialog } from "components/DraggableModalDialog";
import {
  MdBlock,
  MdCheckCircle,
  MdHeadset,
  MdFolderOpen,
  MdInfoOutline,
  MdLock,
} from "react-icons/md";
import { showNotification } from "services/NotificationService";
import { formatPhoneNumber, getErrorMessage } from "services/UtilityService";
import {
  fetchPhoneNumberSuppression,
  putPhoneNumberSuppression,
} from "./aiCallLogApi";
import {
  getCallGroupActivityUrl,
  getPolyStudioConversationUrl,
} from "./buildAiCallLogLinks";

const BLOCK_REASON_INVALID = "INVALID";

/** Display-friendly US phone; falls back to raw value when not 10/11 digits. */
function displayPhoneNumber(phone) {
  if (phone == null || phone === "") return "—";
  const digits = String(phone).replace(/\D/g, "");
  const ten =
    digits.length === 11 && digits.startsWith("1")
      ? digits.slice(1)
      : digits.length === 10
        ? digits
        : null;
  if (!ten) return String(phone);
  return formatPhoneNumber(ten) || String(phone);
}

/**
 * @param {{
 *   show: boolean,
 *   onHide: () => void,
 *   outboundPhoneNumber?: string | null,
 *   aiCallSid?: string | null,
 *   callGroupUuid?: string | null,
 * }} props
 */
export default function AiCallLogDetailsModal({
  show,
  onHide,
  outboundPhoneNumber,
  aiCallSid,
  callGroupUuid,
}) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  /** Desired blocked state after confirm; null when no confirm pending. */
  const [pendingBlocked, setPendingBlocked] = useState(null);

  const phoneNumber =
    outboundPhoneNumber != null && String(outboundPhoneNumber).trim() !== ""
      ? String(outboundPhoneNumber).trim()
      : null;

  useEffect(() => {
    if (!show) {
      setStatus(null);
      setLoadError(null);
      setLoading(false);
      setActionLoading(false);
      setPendingBlocked(null);
      return;
    }
    if (!phoneNumber) {
      setStatus(null);
      setLoadError("No outbound phone number on this call.");
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setStatus(null);
    setPendingBlocked(null);

    fetchPhoneNumberSuppression(phoneNumber)
      .then((data) => {
        if (cancelled) return;
        setStatus(data);
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadError(getErrorMessage(e?.data) || getErrorMessage("SOMETHING_WENT_WRONG"));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show, phoneNumber]);

  const listenUrl = getPolyStudioConversationUrl(aiCallSid);
  const callGroupUrl = getCallGroupActivityUrl(callGroupUuid);

  const isBlocked = status?.active === true;
  const isInvalid = isBlocked && status?.blockReason === BLOCK_REASON_INVALID;
  const toggleDisabled = !status || actionLoading || isInvalid || !!loadError;

  const handleToggleChange = (checked) => {
    if (toggleDisabled || checked === isBlocked) return;
    setPendingBlocked(checked);
  };

  const handleConfirmCancel = () => {
    setPendingBlocked(null);
  };

  const handleConfirmSave = () => {
    if (!phoneNumber || pendingBlocked == null || actionLoading) return;
    const shouldBlock = pendingBlocked;
    setPendingBlocked(null);
    setActionLoading(true);

    putPhoneNumberSuppression(phoneNumber, shouldBlock)
      .then((data) => {
        setStatus(data);
        showNotification({
          title: shouldBlock ? "AI outreach blocked" : "AI outreach unblocked",
          message: shouldBlock
            ? "This phone number is excluded from future AI calls."
            : "This phone number is no longer blocked for AI outreach.",
          position: "bl",
          type: "success",
        });
      })
      .catch((e) => {
        showNotification({
          title: shouldBlock
            ? "Unable to block phone number"
            : "Unable to unblock phone number",
          message: getErrorMessage(e?.data) || getErrorMessage("SOMETHING_WENT_WRONG"),
          position: "bl",
          type: "error",
        });
        return fetchPhoneNumberSuppression(phoneNumber)
          .then((data) => setStatus(data))
          .catch(() => {});
      })
      .finally(() => setActionLoading(false));
  };

  const confirmTitle = pendingBlocked
    ? "Block AI outreach?"
    : "Unblock AI outreach?";
  const confirmBody = pendingBlocked
    ? `Block ${displayPhoneNumber(phoneNumber)} from future AI calls? All call groups for this number will be excluded.`
    : `Unblock ${displayPhoneNumber(phoneNumber)} for future AI outreach?`;
  const confirmLabel = pendingBlocked ? "Block" : "Unblock";
  const confirmVariant = pendingBlocked ? "danger" : "primary";

  const sectionIcon = isInvalid ? (
    <MdBlock size={22} className="text-warning" aria-hidden />
  ) : isBlocked ? (
    <MdBlock size={22} className="text-danger" aria-hidden />
  ) : (
    <MdCheckCircle size={22} className="text-success" aria-hidden />
  );

  const statusBadge = !status ? null : isInvalid ? (
    <Badge
      pill
      bg="warning"
      text="dark"
      className="fw-normal"
      data-testid="ai-call-log-details-status"
    >
      Blocked · Automatic
    </Badge>
  ) : isBlocked ? (
    <Badge
      pill
      bg="danger"
      className="fw-normal"
      data-testid="ai-call-log-details-status"
    >
      Blocked
    </Badge>
  ) : (
    <Badge
      pill
      bg="success"
      className="fw-normal"
      data-testid="ai-call-log-details-status"
    >
      Not blocked
    </Badge>
  );

  const statusDescription = isInvalid
    ? "AI outbound calls are disabled for this phone number across all call groups."
    : isBlocked
      ? "Future AI outbound calls to this phone number are disabled across all call groups."
      : "Future AI outbound calls to this phone number are allowed across all call groups.";

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        centered
        dialogAs={DraggableModalDialog}
        data-testid="ai-call-log-details-modal"
      >
        <Modal.Header closeButton style={{ cursor: "move" }}>
          <Modal.Title data-testid="ai-call-log-details-title">Call Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex align-items-center gap-2 mb-3">
            {sectionIcon}
            <h6 className="mb-0">AI Outreach</h6>
          </div>

          {loading && <Spinner />}

          {!loading && loadError && <Alert variant="danger">{loadError}</Alert>}

          {!loading && !loadError && (
            <>
              <div className="mb-3">
                <div className="text-muted small mb-1">Status</div>
                {statusBadge || <span className="text-muted">—</span>}
              </div>

              <p
                className="text-muted small mb-3"
                style={{ maxWidth: 360 }}
                data-testid="ai-call-log-details-description"
              >
                {statusDescription}
              </p>

              <div className="mb-3">
                <div className="text-muted small mb-1">Phone Number</div>
                <div
                  className="fw-semibold"
                  data-testid="ai-call-log-details-phone"
                >
                  {displayPhoneNumber(phoneNumber)}
                </div>
              </div>

              {isInvalid && (
                <Alert
                  variant="warning"
                  className="mb-3 py-2 small"
                  data-testid="ai-call-log-details-invalid-callout"
                >
                  <div className="d-flex align-items-start gap-2">
                    <MdInfoOutline size={16} className="mt-1 flex-shrink-0" aria-hidden />
                    <div>
                      <div className="fw-semibold mb-1">Managed automatically</div>
                      <div className="mb-0">
                        This phone number has been marked as undialable (invalid).
                        AI outreach cannot be enabled until the phone number is
                        corrected.
                      </div>
                    </div>
                  </div>
                </Alert>
              )}

              {status && (
                <div>
                  <Form.Check
                    type="switch"
                    id="ai-outreach-blocked-switch"
                    className="rm-switch-danger"
                    label={actionLoading ? "Saving…" : "Block AI Outreach"}
                    checked={isBlocked}
                    disabled={toggleDisabled}
                    onChange={(e) => handleToggleChange(e.target.checked)}
                    data-testid="ai-call-log-details-block-switch"
                  />
                  {isInvalid && (
                    <div
                      className="d-flex align-items-center gap-1 text-muted small mt-1 ms-1"
                      data-testid="ai-call-log-details-lock-hint"
                    >
                      <MdLock size={14} aria-hidden />
                      <span>This setting is managed automatically.</span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          <hr className="my-4" />

          <div className="d-flex flex-wrap gap-2">
            {listenUrl ? (
              <Button
                size="sm"
                variant="outline-primary"
                href={listenUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="d-inline-flex align-items-center gap-1"
                data-testid="ai-call-log-details-listen"
              >
                <MdHeadset size={16} aria-hidden />
                Listen Recording
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline-primary"
                disabled
                className="d-inline-flex align-items-center gap-1"
                data-testid="ai-call-log-details-listen"
              >
                <MdHeadset size={16} aria-hidden />
                Listen Recording
              </Button>
            )}
            {callGroupUrl ? (
              <Button
                size="sm"
                variant="outline-primary"
                href={callGroupUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="d-inline-flex align-items-center gap-1"
                data-testid="ai-call-log-details-call-group"
              >
                <MdFolderOpen size={16} aria-hidden />
                View Call Group
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline-primary"
                disabled
                className="d-inline-flex align-items-center gap-1"
                data-testid="ai-call-log-details-call-group"
              >
                <MdFolderOpen size={16} aria-hidden />
                View Call Group
              </Button>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={onHide}
            data-testid="ai-call-log-details-close"
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmationModal
        show={pendingBlocked != null}
        onHide={handleConfirmCancel}
        onCancel={handleConfirmCancel}
        onConfirm={handleConfirmSave}
        title={confirmTitle}
        body={confirmBody}
        confirmLabel={confirmLabel}
        confirmVariant={confirmVariant}
        size="sm"
        dataTestId="ai-call-log-block-confirm"
      />
    </>
  );
}
